/* eslint-disable check-i18n-id-prefix/i18n-id-prefix-with-package-name-defineMessages */
import type React from 'react';
import { useEffect } from 'react';
import type { IntlShape } from 'react-intl-next';
import { defineMessages, useIntl } from 'react-intl-next';

import type { ProviderError } from '@atlaskit/collab-provider';

import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';

export type NativeCollabErrorFlagProps = {
	collabEditError?: ProviderError;
	collabRecoveryError?: Error | { message: string; status: number }; // Can't import CollabRecoveryErrorType due to circular dep
	heartbeatError?: Error | { message: string; reason: string }; // For testing -- can't import CollabHeartbeatError due to circular dep
	saveDraftError?: Error & { statusCode?: number };
};

export enum NATIVE_COLLAB_ERROR_REASONS {
	// Native collab errors propagate from Confluence BE in S2S request to permission refresh and then to websocket via atlassian-frontend/src/packages/editor/collab-provider/src/error-code-mapper.ts
	RESOURCE_DELETED = 'RESOURCE_DELETED',
}

const i18n = defineMessages({
	// eslint-disable-next-line @atlassian/i18n/require-description
	recoveryErrorTitle: {
		id: 'editor-flags.error.recovery.title',
		defaultMessage: 'Something went wrong while loading the editor',
	},
	// eslint-disable-next-line @atlassian/i18n/require-description
	recoveryErrorBody: {
		id: 'editor-flags.error.recovery.body',
		defaultMessage: 'Your changes are safe. Please refresh and try again.',
	},
	siteMaintenanceTitle: {
		id: 'editor-flags.error.tenant.suspended.title',
		defaultMessage: 'You can’t edit this content right now',
		description:
			'Title for an error flag that appears when the Confluence site is undergoing maintenance and cannot be used.',
	},
	siteMaintenanceDescription: {
		id: 'editor-flags.error.tenant.suspended.description',
		defaultMessage: 'Your Atlassian Cloud site is unavailable due to scheduled maintenance.',
		description:
			'Body of the error flag that appears when the Confluence site is undergoing maintenance and cannot be used.',
	},
	permissionRemovedTitle: {
		id: 'fabric.editor.permission.removed.title',
		defaultMessage: 'We can’t show this content',
		description:
			"Title for an error flag that appears when the user's permission to edit the content has been removed.",
	},
	permissionRemovedDescription: {
		id: 'fabric.editor.permission.removed.description',
		defaultMessage: 'The permissions for this content were changed. Contact an admin for help.',
		description:
			"Body of the error flag that appears when the user's permission to edit the content has been removed.",
	},
	pageRemovedTitle: {
		id: 'fabric.editor.page.removed.title',
		defaultMessage: 'This page was recently archived or deleted',
		description: 'Title for an error flag that appears when the page has been deleted.',
	},
	pageRemovedDescription: {
		id: 'fabric.editor.page.removed.description',
		defaultMessage: 'Please refresh now.',
		description: 'Body of the error flag that appears when the page has been deleted.',
	},
	genericServerErrorTitle: {
		id: 'error.server-error.title',
		defaultMessage: 'Something went wrong',
		description:
			'Title for an error flag that appears when the server returns an unknown/unspecified unrecoverable error.',
	},
	genericServerErrorBody: {
		id: 'error.server-error.body',
		defaultMessage: 'Please try again later.',
		description:
			'Body of the error flag that appears when the server returns an unrecoverable error.',
	},
});

/**
 * Currently collab service errors are handled based on error status code, in the future we may want finer granularity
 * and use the error message to provide further information in flag description. See also EditorWrapper.tsx & EditorFlags.tsx
 */
const collabEditErrorFlagContent = (
	formatMessage: IntlShape['formatMessage'],
	statusCode?: number,
	reason?: string,
) => {
	switch (statusCode) {
		case 403:
			if (reason === NATIVE_COLLAB_ERROR_REASONS.RESOURCE_DELETED) {
				return {
					title: formatMessage(i18n.pageRemovedTitle),
					description: formatMessage(i18n.pageRemovedDescription),
				};
			}
			return {
				title: formatMessage(i18n.permissionRemovedTitle),
				description: formatMessage(i18n.permissionRemovedDescription),
			};
		case 423: // tenant suspended / maintenance
			return {
				title: formatMessage(i18n.siteMaintenanceTitle),
				description: formatMessage(i18n.siteMaintenanceDescription),
			};
		case 500: // Generic error for unrecoverable 500s from Collab Service...
			return {
				title: formatMessage(i18n.genericServerErrorTitle),
				description: formatMessage(i18n.genericServerErrorBody),
			};
		default:
			break;
	}
};

const heartbeatErrorFlagContent = (formatMessage: IntlShape['formatMessage'], reason?: string) => {
	switch (reason) {
		case 'unauthorised': // remove this when xwork endpoint is deprecated
		case 'UNAUTHORIZED':
			return {
				title: formatMessage(i18n.permissionRemovedTitle),
				description: formatMessage(i18n.permissionRemovedDescription),
			};
		case 'tenant-unavailable':
			return {
				title: formatMessage(i18n.siteMaintenanceTitle),
				description: formatMessage(i18n.siteMaintenanceDescription),
			};
		default:
			break;
	}
};

const confluenceErrorFlagContent = (
	formatMessage: IntlShape['formatMessage'],
	statusCode?: number,
): { description: string; title: string } | undefined => {
	if (!statusCode) {
		return {
			title: formatMessage(i18n.genericServerErrorTitle),
			description: formatMessage(i18n.genericServerErrorBody),
		};
	}
	if (statusCode === 403) {
		return {
			title: formatMessage(i18n.permissionRemovedTitle),
			description: formatMessage(i18n.permissionRemovedDescription),
		};
	} else if (statusCode === 404) {
		return {
			title: formatMessage(i18n.pageRemovedTitle),
			description: formatMessage(i18n.pageRemovedDescription),
		};
	} else if (statusCode === 503) {
		return {
			title: formatMessage(i18n.siteMaintenanceTitle),
			description: formatMessage(i18n.siteMaintenanceDescription),
		};
	}
};

export const NativeCollabFlags: React.ComponentType<
	NativeCollabErrorFlagProps & WithFlagsProps
> = ({
	flags,
	collabEditError,
	collabRecoveryError,
	heartbeatError,
	saveDraftError,
}: NativeCollabErrorFlagProps & WithFlagsProps) => {
	const intl = useIntl();
	useEffect(() => {
		if (collabEditError) {
			const flagContent = collabEditErrorFlagContent(
				intl.formatMessage,
				'status' in collabEditError ? collabEditError.status : undefined,
				'reason' in collabEditError ? collabEditError.reason : undefined,
			);
			if (flagContent) {
				void flags.showErrorFlag({ ...flagContent });
			}
		} // Don't depend on `flags` since its updates will cause infinite spam
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [collabEditError]);

	useEffect(() => {
		if (collabRecoveryError) {
			void flags.showErrorFlag({
				title: intl.formatMessage(i18n.recoveryErrorTitle),
				description: intl.formatMessage(i18n.recoveryErrorBody),
			});
		} // Don't depend on `flags` since its updates will cause infinite spam
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [collabRecoveryError]);

	useEffect(() => {
		if (heartbeatError) {
			const flagContent = heartbeatErrorFlagContent(
				intl.formatMessage,
				'reason' in heartbeatError ? heartbeatError.reason : undefined,
			);
			if (flagContent) {
				void flags.showErrorFlag({ ...flagContent });
			}
		} // Don't depend on `flags` since its updates will cause infinite spam
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [heartbeatError]);

	useEffect(() => {
		if (saveDraftError) {
			const flagContent = confluenceErrorFlagContent(intl.formatMessage, saveDraftError.statusCode);
			if (flagContent) {
				void flags.showErrorFlag({ ...flagContent });
			}
		} // You guessed it! .....Don't depend on `flags` since its updates will cause infinite spam
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [saveDraftError]);

	// Cleanup on component unmount
	useEffect(() => () => void flags.hideAllFlags(), [flags]);
	return null;
};

/**
 * Component that handles displaying error message flags for editor when appropriate. These errors happen upon loading the editor or
 * during the editing session.
 */
export const NativeCollabErrorFlags = withFlags<NativeCollabErrorFlagProps>(NativeCollabFlags);
